
// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====

/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Switch,
  Alert,
  Image,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import AsyncStorage from '@react-native-async-storage/async-storage';

// JOBENG Logo component using the app icon
const JobengLogo = () => (
  <View style={styles.logoContainer}>
    <Image
      source={require('../../../../assets/jobeng_icon.png')}
      style={styles.logoImage}
      resizeMode="contain"
    />
    <Text style={styles.logoText}>JOBENG</Text>
  </View>
);

const ScheduleSettingsScreen = ({ navigation, route }) => {
  const { campaign } = route.params;

  // Parse dates from campaign
  const initialStartDate = campaign?.startDate
    ? new Date(campaign.startDate)
    : new Date();
  const initialEndDate = campaign?.endDate
    ? new Date(campaign.endDate)
    : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  // Form state
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);

  // Time scheduling
  const [enableTimeScheduling, setEnableTimeScheduling] = useState(
    campaign?.scheduling?.enableTimeScheduling || false
  );
  const [startTime, setStartTime] = useState(
    campaign?.scheduling?.startTime
      ? new Date(`2025-01-01T${campaign.scheduling.startTime}:00`)
      : new Date(2025, 0, 1, 8, 0)
  );
  const [endTime, setEndTime] = useState(
    campaign?.scheduling?.endTime
      ? new Date(`2025-01-01T${campaign.scheduling.endTime}:00`)
      : new Date(2025, 0, 1, 20, 0)
  );
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  // Day scheduling
  const [enableDayScheduling, setEnableDayScheduling] = useState(
    campaign?.scheduling?.enableDayScheduling || false
  );
  const [selectedDays, setSelectedDays] = useState(
    campaign?.scheduling?.selectedDays || [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
    ]
  );

  // Pause scheduling
  const [isPaused, setIsPaused] = useState(
    campaign?.scheduling?.isPaused || false
  );
  const [pauseStartDate, setPauseStartDate] = useState(
    campaign?.scheduling?.pauseStartDate
      ? new Date(campaign.scheduling.pauseStartDate)
      : new Date()
  );
  const [pauseEndDate, setPauseEndDate] = useState(
    campaign?.scheduling?.pauseEndDate
      ? new Date(campaign.scheduling.pauseEndDate)
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  );
  const [showPauseStartDatePicker, setShowPauseStartDatePicker] =
    useState(false);
  const [showPauseEndDatePicker, setShowPauseEndDatePicker] = useState(false);

  const days = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  const handleStartDateChange = (event, selectedDate) => {
    setShowStartDatePicker(false);
    if (selectedDate) {
      setStartDate(selectedDate);
      // Ensure end date is after start date
      if (selectedDate > endDate) {
        setEndDate(new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000)); // Set to day after start date
      }
    }
  };

  const handleEndDateChange = (event, selectedDate) => {
    setShowEndDatePicker(false);
    if (selectedDate && selectedDate >= startDate) {
      setEndDate(selectedDate);
    } else if (selectedDate) {
      Alert.alert('Invalid Date', 'End date must be after start date');
    }
  };

  const handleStartTimeChange = (event, selectedTime) => {
    setShowStartTimePicker(false);
    if (selectedTime) {
      setStartTime(selectedTime);
      // Ensure end time is after start time
      if (selectedTime > endTime) {
        setEndTime(new Date(selectedTime.getTime() + 60 * 60 * 1000)); // Set to 1 hour after start time
      }
    }
  };

  const handleEndTimeChange = (event, selectedTime) => {
    setShowEndTimePicker(false);
    if (selectedTime && selectedTime >= startTime) {
      setEndTime(selectedTime);
    } else if (selectedTime) {
      Alert.alert('Invalid Time', 'End time must be after start time');
    }
  };

  const handlePauseStartDateChange = (event, selectedDate) => {
    setShowPauseStartDatePicker(false);
    if (selectedDate) {
      setPauseStartDate(selectedDate);
      // Ensure pause end date is after pause start date
      if (selectedDate > pauseEndDate) {
        setPauseEndDate(new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000)); // Set to day after pause start date
      }
    }
  };

  const handlePauseEndDateChange = (event, selectedDate) => {
    setShowPauseEndDatePicker(false);
    if (selectedDate && selectedDate >= pauseStartDate) {
      setPauseEndDate(selectedDate);
    } else if (selectedDate) {
      Alert.alert(
        'Invalid Date',
        'Pause end date must be after pause start date'
      );
    }
  };

  const toggleDay = day => {
    const newSelectedDays = [...selectedDays];
    if (newSelectedDays.includes(day)) {
      // Remove if already selected
      if (newSelectedDays.length > 1) {
        // Ensure at least one day is selected
        setSelectedDays(newSelectedDays.filter(d => d !== day));
      } else {
        Alert.alert('Selection Required', 'At least one day must be selected');
      }
    } else {
      // Add if not selected
      setSelectedDays([...newSelectedDays, day]);
    }
  };

  const formatDate = date => {
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const formatTime = date => {
    return date.toTimeString().split(' ')[0].substring(0, 5); // Format as HH:MM
  };

  const handleSave = async () => {
    // Save schedule settings via API
    try {
      const companyId = await getCompanyId();
      await apiRequest('schedule-settings', {
        method: 'PUT',
        body: {
          campaign_id: campaign?.id,
          company_id: companyId,
          startDate, endDate,
          enableTimeScheduling, startTime, endTime,
          enableDayScheduling, selectedDays,
          isPaused, pauseStartDate, pauseEndDate,
        }
      });
    } catch (error) {
      console.warn('API save failed, saving locally:', error.message);
    }

    // Create updated campaign object with new schedule settings
    const updatedCampaign = {
      ...campaign,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      scheduling: {
        enableTimeScheduling,
        startTime: enableTimeScheduling ? formatTime(startTime) : null,
        endTime: enableTimeScheduling ? formatTime(endTime) : null,
        enableDayScheduling,
        selectedDays: enableDayScheduling ? selectedDays : days,
        isPaused,
        pauseStartDate: isPaused ? formatDate(pauseStartDate) : null,
        pauseEndDate: isPaused ? formatDate(pauseEndDate) : null,
      },
    };

    // Navigate back to campaign details with updated campaign
    navigation.navigate('CampaignDetails', { campaign: updatedCampaign });
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Schedule Settings</Text>
        <JobengLogo />
      </View>

      {/* Content */}
      <ScrollView style={styles.scrollContainer}>
        <View style={styles.content}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Campaign Duration</Text>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Start Date*</Text>
              <TouchableOpacity
                style={styles.datePickerButton}
                onPress={() => setShowStartDatePicker(true)}
              >
                <MaterialCommunityIcons
                  name="calendar"
                  size={20}
                  color="#0e4e96"
                />
                <Text style={styles.datePickerButtonText}>
                  {formatDate(startDate)}
                </Text>
              </TouchableOpacity>
              {showStartDatePicker && (
                <DateTimePicker
                  value={startDate}
                  mode="date"
                  display="default"
                  onChange={handleStartDateChange}
                  minimumDate={new Date()}
                />
              )}
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>End Date*</Text>
              <TouchableOpacity
                style={styles.datePickerButton}
                onPress={() => setShowEndDatePicker(true)}
              >
                <MaterialCommunityIcons
                  name="calendar"
                  size={20}
                  color="#0e4e96"
                />
                <Text style={styles.datePickerButtonText}>
                  {formatDate(endDate)}
                </Text>
              </TouchableOpacity>
              {showEndDatePicker && (
                <DateTimePicker
                  value={endDate}
                  mode="date"
                  display="default"
                  onChange={handleEndDateChange}
                  minimumDate={startDate}
                />
              )}
            </View>

            <View style={styles.durationInfo}>
              <MaterialCommunityIcons
                name="clock-outline"
                size={20}
                color="#0e4e96"
              />
              <Text style={styles.durationText}>
                Campaign will run for{' '}
                {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} days
              </Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Time Scheduling</Text>

            <View style={styles.switchContainer}>
              <View style={styles.switchTextContainer}>
                <Text style={styles.switchLabel}>
                  Enable Time-of-Day Scheduling
                </Text>
                <Text style={styles.switchDescription}>
                  Limit your campaign to specific hours of the day
                </Text>
              </View>
              <Switch
                value={enableTimeScheduling}
                onValueChange={setEnableTimeScheduling}
                trackColor={{ false: '#ccc', true: '#a7c8f2' }}
                thumbColor={enableTimeScheduling ? '#0e4e96' : '#f4f3f4'}
              />
            </View>

            {enableTimeScheduling && (
              <>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Start Time</Text>
                  <TouchableOpacity
                    style={styles.datePickerButton}
                    onPress={() => setShowStartTimePicker(true)}
                  >
                    <MaterialCommunityIcons
                      name="clock-outline"
                      size={20}
                      color="#0e4e96"
                    />
                    <Text style={styles.datePickerButtonText}>
                      {formatTime(startTime)}
                    </Text>
                  </TouchableOpacity>
                  {showStartTimePicker && (
                    <DateTimePicker
                      value={startTime}
                      mode="time"
                      display="default"
                      onChange={handleStartTimeChange}
                    />
                  )}
                </View>

                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>End Time</Text>
                  <TouchableOpacity
                    style={styles.datePickerButton}
                    onPress={() => setShowEndTimePicker(true)}
                  >
                    <MaterialCommunityIcons
                      name="clock-outline"
                      size={20}
                      color="#0e4e96"
                    />
                    <Text style={styles.datePickerButtonText}>
                      {formatTime(endTime)}
                    </Text>
                  </TouchableOpacity>
                  {showEndTimePicker && (
                    <DateTimePicker
                      value={endTime}
                      mode="time"
                      display="default"
                      onChange={handleEndTimeChange}
                    />
                  )}
                </View>

                <View style={styles.noteContainer}>
                  <MaterialCommunityIcons
                    name="information-outline"
                    size={20}
                    color="#666"
                  />
                  <Text style={styles.noteText}>
                    Your campaign will only run between {formatTime(startTime)}{' '}
                    and {formatTime(endTime)} each day.
                  </Text>
                </View>
              </>
            )}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Day Scheduling</Text>

            <View style={styles.switchContainer}>
              <View style={styles.switchTextContainer}>
                <Text style={styles.switchLabel}>
                  Enable Day-of-Week Scheduling
                </Text>
                <Text style={styles.switchDescription}>
                  Limit your campaign to specific days of the week
                </Text>
              </View>
              <Switch
                value={enableDayScheduling}
                onValueChange={setEnableDayScheduling}
                trackColor={{ false: '#ccc', true: '#a7c8f2' }}
                thumbColor={enableDayScheduling ? '#0e4e96' : '#f4f3f4'}
              />
            </View>

            {enableDayScheduling && (
              <View style={styles.daysContainer}>
                {days.map(day => (
                  <TouchableOpacity
                    key={`day-${day}`}
                    style={[
                      styles.dayButton,
                      selectedDays.includes(day) && styles.selectedDayButton,
                    ]}
                    onPress={() => toggleDay(day)}
                  >
                    <Text
                      style={[
                        styles.dayButtonText,
                        selectedDays.includes(day) &&
                          styles.selectedDayButtonText,
                      ]}
                    >
                      {day.substring(0, 3)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Campaign Pause</Text>

            <View style={styles.switchContainer}>
              <View style={styles.switchTextContainer}>
                <Text style={styles.switchLabel}>Pause Campaign</Text>
                <Text style={styles.switchDescription}>
                  Temporarily pause your campaign during a specific period
                </Text>
              </View>
              <Switch
                value={isPaused}
                onValueChange={setIsPaused}
                trackColor={{ false: '#ccc', true: '#a7c8f2' }}
                thumbColor={isPaused ? '#0e4e96' : '#f4f3f4'}
              />
            </View>

            {isPaused && (
              <>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Pause Start Date</Text>
                  <TouchableOpacity
                    style={styles.datePickerButton}
                    onPress={() => setShowPauseStartDatePicker(true)}
                  >
                    <MaterialCommunityIcons
                      name="calendar"
                      size={20}
                      color="#0e4e96"
                    />
                    <Text style={styles.datePickerButtonText}>
                      {formatDate(pauseStartDate)}
                    </Text>
                  </TouchableOpacity>
                  {showPauseStartDatePicker && (
                    <DateTimePicker
                      value={pauseStartDate}
                      mode="date"
                      display="default"
                      onChange={handlePauseStartDateChange}
                      minimumDate={new Date()}
                    />
                  )}
                </View>

                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Pause End Date</Text>
                  <TouchableOpacity
                    style={styles.datePickerButton}
                    onPress={() => setShowPauseEndDatePicker(true)}
                  >
                    <MaterialCommunityIcons
                      name="calendar"
                      size={20}
                      color="#0e4e96"
                    />
                    <Text style={styles.datePickerButtonText}>
                      {formatDate(pauseEndDate)}
                    </Text>
                  </TouchableOpacity>
                  {showPauseEndDatePicker && (
                    <DateTimePicker
                      value={pauseEndDate}
                      mode="date"
                      display="default"
                      onChange={handlePauseEndDateChange}
                      minimumDate={pauseStartDate}
                    />
                  )}
                </View>

                <View style={styles.warningContainer}>
                  <MaterialCommunityIcons
                    name="alert-circle-outline"
                    size={20}
                    color="#fdb913"
                  />
                  <Text style={styles.warningText}>
                    Your campaign will be paused from{' '}
                    {formatDate(pauseStartDate)} to {formatDate(pauseEndDate)}.
                    No ads will run during this period.
                  </Text>
                </View>
              </>
            )}
          </View>

          <View style={styles.noteContainer}>
            <MaterialCommunityIcons
              name="information-outline"
              size={20}
              color="#666"
            />
            <Text style={styles.noteText}>
              Schedule changes may take up to 1 hour to take effect across all
              platforms.
            </Text>
          </View>
        </View>
      </ScrollView>

      {/* Action Buttons */}
      <View style={styles.actionContainer}>
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={() => navigation.goBack()}
        >
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
          <Text style={styles.saveButtonText}>Save Changes</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoImage: {
    width: 30,
    height: 30,
    marginRight: 5,
  },
  logoText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  scrollContainer: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  inputGroup: {
    marginBottom: 15,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  datePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
  },
  datePickerButtonText: {
    fontSize: 16,
    color: '#333',
    marginLeft: 10,
  },
  durationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f7ff',
    padding: 12,
    borderRadius: 8,
  },
  durationText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 10,
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  switchTextContainer: {
    flex: 1,
  },
  switchLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  switchDescription: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  daysContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  dayButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f9f9f9',
    borderWidth: 1,
    borderColor: '#ddd',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedDayButton: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  dayButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#333',
  },
  selectedDayButtonText: {
    color: '#fff',
  },
  noteContainer: {
    flexDirection: 'row',
    backgroundColor: '#f9f9f9',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 15,
  },
  noteText: {
    fontSize: 14,
    color: '#666',
    marginLeft: 10,
    flex: 1,
  },
  warningContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff9e6',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 15,
  },
  warningText: {
    fontSize: 14,
    color: '#d97706',
    marginLeft: 10,
    flex: 1,
  },
  actionContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 20,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#e1e1e1',
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  saveButton: {
    backgroundColor: '#fdb913', // Canary Yellow
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  saveButtonText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: 'bold',
  },
});

export default ScheduleSettingsScreen;
